import { useCallback } from "react"
import { z } from "zod"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"

const MAX_RECENTS = 5

const recentFilterSchema = z.union([
  z.object({
    kind: z.literal("field"),
    value: z.string(),
    dynamicValue: z.string().optional(),
  }),
  z.object({
    kind: z.literal("quick"),
    value: z.string(),
    isNonBoolean: z.boolean(),
  }),
])

const recentFiltersSchema = z.array(recentFilterSchema)

type RecentFilterEntry = z.infer<typeof recentFilterSchema>

export function useRecentFilters() {
  const [recents, setRecents] = useSafeLocalStorage<RecentFilterEntry[]>(
    "filterPopover.recents",
    [],
    recentFiltersSchema,
  )

  const recordRecent = useCallback(
    (entry: RecentFilterEntry) => {
      const isSame = (a: RecentFilterEntry, b: RecentFilterEntry) => {
        if (a.kind !== b.kind) return false
        if (a.kind === "field" && b.kind === "field") {
          return a.value === b.value && a.dynamicValue === b.dynamicValue
        }
        if (a.kind === "quick" && b.kind === "quick") {
          return a.value === b.value && a.isNonBoolean === b.isNonBoolean
        }
        return false
      }
      const next = [entry, ...recents.filter((r) => !isSame(r, entry))].slice(0, MAX_RECENTS)
      setRecents(next)
    },
    [recents, setRecents],
  )

  return { recents, recordRecent }
}
